import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { OpcionesSelect } from '../../../common/components/icono-filtro/interface/opciones-select.interface';
import {
  TypeaheadResolveFn,
  TypeaheadSearchFn,
} from '../input-typeahead/input-typeahead.component';

export interface FiltroColumnaEvento {
  campo: string;
  valor: string;
}

/**
 * Tabla reutilizable para listados con filtros superiores, filtros por columna (`app-icono-filtro`)
 * y paginación. Es solo **presentación**: no llama APIs; el contenedor padre construye `columns`,
 * `data` y `filters` y reacciona a los `@Output`.
 *
 * **Entradas principales:** `columns`, `data`, `filters`. Opcionalmente paginación (`total`, `page`,
 * `pageSize`, …) y `styleFilters` para el grid del bloque de filtros.
 *
 * **Salidas:** `pageChange`, `pageSizeChange`, `filtersApply` (filtros superiores + Aplicar),
 * `filtersUpdate` (cambio de un select superior: mapa campo → valor),
 * `filtroColumna` (filtro atómico por columna: `{ campo, valor }`),
 * `filtersChange` (estado completo de todos los filtros de columna activos).
 */
@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.css']
})
export class TableComponent implements OnChanges, OnDestroy {

  @Input()  filters: unknown[] = [];
  @Input()  data: unknown[] | null = null;
  @Input()  columns: any[] = [];
  @Input()  total = 0;
  @Input()  page = 1;
  @Input()  pageSize = 10;
  @Input()  pageSizeOptions: number[] = [10, 20, 50, 100];
  @Input()  styleFilters: string = 'col-md-2';
  @Input()  textoRegistros = 'registros';
  @Input()  textoMostrar = 'Mostrar';
  @Input()  mostrarEnlaceRelacionar = false;
  @Input()  mostrarBotonLimpiar = true;
  @Input()  mostrarBotonAplicar = true;
  @Input()  mostrarBotonEditar = false;
  @Input()  editFilter = false;
  /** Primera columna con checkbox por fila y “seleccionar página” en cabecera. */
  @Input()  showCheckboxes = false;
  /** Campo del objeto fila usado como identificador de selección (p. ej. `id`). */
  @Input()  rowIdField = 'id';

  @Output() pageChange     = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();
  @Output() filtersApply   = new EventEmitter<unknown[]>();
  @Output() filtersUpdate = new EventEmitter<Record<string, string>>();

  /** Emite el filtro atómico: qué columna cambió y su nuevo valor. */
  @Output() filtroColumna = new EventEmitter<FiltroColumnaEvento>();

  /**
   * Emite el estado completo en JSON de todos los filtros de columna activos.
   * Los campos sin valor (vacíos) se excluyen automáticamente.
   * Útil para construir queries con múltiples condiciones simultáneas.
   */
  @Output() filtersChange = new EventEmitter<{
    filter: Record<string, string>;
    page: number;
    pageSize: number;
    cleanFilters?: boolean;
  }>();

  /** IDs de filas seleccionadas (persisten al cambiar de página). */
  @Output() selectionChange = new EventEmitter<string[]>();

  paginas: number[] = [];
  totalPaginas = 1;
  defaultFilter: unknown[] = [];

  /** Estado interno de los filtros de columna. */
  private _activeFilters: Record<string, string> = {};
  private readonly _selectedRowKeys = new Set<string>();
  /** Espera entre teclas antes de notificar el filtro de texto. */
  private readonly inputFilterDelayMs = 2000;
  private inputFilterTimer: ReturnType<typeof setTimeout> | null = null;

  get rangoDesde(): number {
    return (this.page - 1) * this.pageSize + 1;
  }
  get rangoHasta(): number {
    return Math.min(this.page * this.pageSize, this.total);
  }

  get colspanTabla(): number {
    return this.columns.length + (this.showCheckboxes ? 1 : 0);
  }

  get allRowsOnPageSelected(): boolean {
    const rows = this.data ?? [];
    if (!rows.length) {
      return false;
    }
    return rows.every((row) => this.isRowSelected(row));
  }

  get usaLayoutFijo(): boolean {
    if (this.showCheckboxes) {
      return true;
    }
    return (this.columns ?? []).some(
      (column) => this.getColumnWidth(column) != null
    );
  }

  ngOnDestroy(): void {
    this.cancelarInputFilterTimer();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filters']?.currentValue) {
      const incoming = changes['filters'].currentValue as unknown[];
      if (Array.isArray(incoming) && incoming.length > 0) {
        this.defaultFilter = this.cloneFilters(incoming);
      }
    }
    if (changes['columns']?.currentValue) {
      this.inicializarFiltrosColumnaPorDefecto(
        changes['columns'].currentValue as unknown[],
      );
    }
    if (changes['total'] || changes['pageSize'] || changes['page']) {
      this.calcularPaginacion();
    }
  }

  calcularPaginacion(): void {
    this.totalPaginas = Math.max(1, Math.ceil(this.total / this.pageSize));
    const ventana = 2;
    const inicio = Math.max(1, this.page - ventana);
    const fin    = Math.min(this.totalPaginas, this.page + ventana);
    this.paginas = Array.from({ length: fin - inicio + 1 }, (_, i) => inicio + i);
  }

  cambiarPagina(p: number): void {
    if (p < 1 || p > this.totalPaginas || p === this.page) return;
    this.page = p;
    this.pageChange.emit(this.page);
    this.filtersChange.emit({ filter: { ...this._activeFilters, ...this._buildFiltrosSuperiores() }, page: this.page, pageSize: this.pageSize });
  }

  onPaginaAnterior(): void {
    this.cambiarPagina(this.page - 1);
  }

  onPaginaSiguiente(): void {
    this.cambiarPagina(this.page + 1);
  }

  onCambiarLimite(nuevoLimite: number): void {
    this.pageSize = nuevoLimite;
    this.onPageSizeChange();
  }

  onPageSizeChange(): void {
    this.page = 1;
    this.calcularPaginacion();
    this.pageSizeChange.emit(this.pageSize);
    this.pageChange.emit(1);
    this.filtersChange.emit({ filter: { ...this._activeFilters, ...this._buildFiltrosSuperiores() }, page: this.page, pageSize: this.pageSize });
  }

  onEditar(): void {
    return;
  }

  applyFilters(): void {
    this.cancelarInputFilterTimer();
    this.page = 1;
    this.filtersApply.emit(this.filters);
    this.filtersChange.emit({ filter: { ...this._activeFilters, ...this._buildFiltrosSuperiores() }, page: this.page, pageSize: this.pageSize });
  }

  /** Emite el mapa de filtros superiores al cambiar un select (p. ej. subgrupo). */
  onSelectFilterChange(): void {
    this.page = 1;
    this.filtersChange.emit({
      filter: { ...this._activeFilters, ...this._buildFiltrosSuperiores() },
      page: this.page,
      pageSize: this.pageSize,
    });
  }

  /** Typeahead: sincroniza `filter.value` y emite `filtersChange`. */
  onTypeaheadFilterChange(
    value: string | number | null,
    filter: Record<string, unknown>
  ): void {
    filter['value'] = value == null || value === '' ? '' : value;
    const campo = String(filter['campo'] ?? '');
    if (campo) {
      this.filtersUpdate.emit({
        [campo]: value == null || value === '' ? '' : String(value),
      });
    }
    this.onSelectFilterChange();
  }

  getTypeaheadSearchFn(
    filter: Record<string, unknown>
  ): TypeaheadSearchFn | undefined {
    const fn = filter['searchFn'];
    return typeof fn === 'function'
      ? (fn as TypeaheadSearchFn)
      : undefined;
  }

  getTypeaheadResolveFn(
    filter: Record<string, unknown>
  ): TypeaheadResolveFn | undefined {
    const fn = filter['resolveFn'];
    return typeof fn === 'function'
      ? (fn as TypeaheadResolveFn)
      : undefined;
  }

  getTypeaheadOptions(
    filter: Record<string, unknown>
  ): ReadonlyArray<Record<string, unknown>> {
    const raw = filter['typeaheadOptions'] ?? filter['options'];
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw.map((entry) => this.mapTypeaheadOption(entry));
  }

  getTypeaheadValueKey(filter: Record<string, unknown>): string {
    const key = filter['valueKey'];
    return typeof key === 'string' && key.trim() ? key.trim() : 'id';
  }

  getTypeaheadDisplayKeys(filter: Record<string, unknown>): string[] {
    const keys = filter['displayKeys'];
    if (Array.isArray(keys) && keys.every((k) => typeof k === 'string')) {
      return keys as string[];
    }
    return ['nombre'];
  }

  getTypeaheadMinLength(filter: Record<string, unknown>): number {
    const min = filter['minSearchLength'];
    return typeof min === 'number' && min >= 0 ? min : 1;
  }

  private mapTypeaheadOption(entry: unknown): Record<string, unknown> {
    if (entry !== null && typeof entry === 'object') {
      const obj = entry as Record<string, unknown>;
      if ('label' in obj && 'value' in obj) {
        return {
          ...obj,
          id: obj['value'],
          nombre: String(obj['label'] ?? ''),
        };
      }
      return { ...obj };
    }
    return { id: entry, nombre: String(entry) };
  }

  /**
   * Reinicia la espera en cada tecla y emite el mapa de filtros
   * solo cuando el usuario deja de escribir.
   */
  onInputFilterChange(): void {
    this.cancelarInputFilterTimer();
    this.inputFilterTimer = setTimeout(() => {
      this.inputFilterTimer = null;
      this.page = 1;
      this.filtersChange.emit({
        filter: { ...this._activeFilters, ...this._buildFiltrosSuperiores() },
        page: this.page,
        pageSize: this.pageSize,
      });
    }, this.inputFilterDelayMs);
  }

  private cancelarInputFilterTimer(): void {
    if (this.inputFilterTimer == null) {
      return;
    }
    clearTimeout(this.inputFilterTimer);
    this.inputFilterTimer = null;
  }

  /**
   * [ngValue] compara por referencia/tipo. El valor del filtro suele ser string
   * ("1") y option.value un number (1); sin esto el select no muestra la opción.
   */
  isFilterSelectEmpty(value: unknown): boolean {
    if (value === null || value === undefined) {
      return true;
    }
    return String(value).trim() === '';
  }

  compareFilterValues(selected: unknown, option: unknown): boolean {
    const selectedEmpty = selected == null || selected === '';
    const optionEmpty = option == null || option === '';
    if (selectedEmpty || optionEmpty) {
      return selectedEmpty && optionEmpty;
    }
    return String(selected) === String(option);
  }

  cleanFilters(): void {
    this.cancelarInputFilterTimer();
    this.filters = this.cloneFilters(this.defaultFilter);
    this._activeFilters = {};
    this.clearRowSelection();
    this.page = 1;
    this.calcularPaginacion();  
    this.filtersChange.emit({ filter: {}, page: this.page, pageSize: this.pageSize, cleanFilters: true });
  }

  /** Copia los filtros para no compartir referencia con el estado editado en el formulario. */
  private cloneFilters(source: unknown[]): unknown[] {
    return (source as Array<Record<string, unknown>>).map((f) => ({
      ...f,
      value: this.cloneFilterValue(f['value']),
    }));
  }

  private cloneFilterValue(value: unknown): unknown {
    if (Array.isArray(value)) {
      return [...value];
    }
    return value;
  }

  mostrarIconoFiltro(column: Record<string, unknown>): boolean {
    if (column?.['iconoFiltro'] === false) {
      return false;
    }
    const campo = column?.['campo'];
    return campo != null && String(campo).length > 0;
  }

  tipoIconoFiltro(column: Record<string, unknown>): 'input' | 'number' | 'select' | 'multiselect' {
    const opts = column?.['opciones'] ?? column?.['options'];
    if (
      column?.['tipoFiltro'] === 'multiseleccion' &&
      Array.isArray(opts) &&
      opts.length > 0
    ) {
      return 'multiselect';
    }
    if (column?.['tipoFiltro'] === 'seleccion') {
      return 'select';
    }
    if (column?.['tipoFiltro'] === 'numerico') {
      return 'number';
    }
    return 'input';
  }

  opcionesIconoFiltro(column: Record<string, unknown>): OpcionesSelect[] {
    const raw = column?.['opciones'] ?? column?.['options'];
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw.map((o: unknown) => {
      if (o !== null && typeof o === 'object') {
        const obj = o as Record<string, unknown>;
        if ('id' in obj && 'nombre' in obj) {
          return o as OpcionesSelect;
        }
        if ('label' in obj && 'value' in obj) {
          return {
            id: obj['value'] as string | number,
            nombre: String(obj['label']),
          };
        }
      }
      return { id: o as string | number, nombre: String(o) };
    });
  }

  /**
   * `col-N` (grid Bootstrap) no define ancho en `<th>`/`<td>`.
   * Si `className` incluye `col-N`, devuelve el % equivalente (N/12).
   * También admite `column.width` directo (ej. `'120px'`, `'15%'`).
   */
  getColumnWidth(column: Record<string, unknown>): string | null {
    const width = column?.['width'];
    if (typeof width === 'string' && width.trim()) {
      return width.trim();
    }
    const className = column?.['className'];
    if (typeof className !== 'string') {
      return null;
    }
    const match = className.match(/\bcol-(\d+)\b/);
    if (!match) {
      return null;
    }
    const cols = Number(match[1]);
    if (cols > 0 && cols <= 12) {
      return `${(cols / 12) * 100}%`;
    }
    return null;
  }

  getFilterNgClass(filter: Record<string, unknown>): string | string[] {
    const extra = filter?.['className'];
    if (typeof extra === 'string' && extra.trim()) {
      return [this.styleFilters, extra.trim()];
    }
    return this.styleFilters;
  }

  placeholderIconoFiltro(column: Record<string, unknown>): string {
    const custom = column?.['filtroPlaceholder'];
    if (typeof custom === 'string' && custom.trim()) {
      return custom;
    }
    const titulo = column?.['label'] ?? column?.['titulo'] ?? '';
    return `Filtrar por ${titulo}`;
  }

  valorInicialFiltro(column: Record<string, unknown>): string {
    const inicial = column?.['valorInicial'];
    if (inicial == null || inicial === '') return '';
    return String(inicial);
  }

  private inicializarFiltrosColumnaPorDefecto(columns: unknown[]): void {
    for (const col of columns as Array<Record<string, unknown>>) {
      const campo = col['campo'];
      const inicial = col['valorInicial'];
      if (
        typeof campo === 'string' &&
        inicial != null &&
        String(inicial).trim() !== '' &&
        !(campo in this._activeFilters)
      ) {
        this._activeFilters[campo] = String(inicial);
      }
    }
  }

  /**
   * Llamado por `app-icono-filtro` cuando el usuario escribe/selecciona en una columna.
   *
   * - Actualiza el estado interno `_activeFilters`.
   * - Si el valor está vacío, elimina la clave del mapa (campo limpiado).
   * - Emite `filtroColumna` con el cambio atómico.
   * - Emite `filtersChange` con una copia inmutable del estado completo activo.
   */
  onFiltroColumna(campo: string, valor: string): void {
    if (valor.trim() === '') {
      delete this._activeFilters[campo];
    } else {
      this._activeFilters[campo] = valor;
    }

    this.page = 1;
    this.calcularPaginacion();
    this.filtroColumna.emit({ campo, valor });
    this.pageChange.emit(1);
    this.filtersChange.emit({ filter: { ...this._activeFilters, ...this._buildFiltrosSuperiores() }, page: this.page, pageSize: this.pageSize });
  }

  /**
   * Limpia todos los filtros de columna y notifica al padre.
   * Útil para un botón "Limpiar filtros" en el contenedor.
   */
  clearColumnFilters(): void {
    this.cancelarInputFilterTimer();
    this._activeFilters = {};
    this.filtersChange.emit({ filter: {}, page: this.page, pageSize: this.pageSize });
  
    this.page = 1;
    this.calcularPaginacion();
    this.pageChange.emit(1);
  }

  private _buildFiltrosSuperiores(): Record<string, string> {
    return (this.filters as Array<{ campo: string; value: unknown }>)
      .filter(f => f.value != null && String(f.value).trim() !== '')
      .reduce<Record<string, string>>((acc, f) => {
        acc[f.campo] = String(f.value);
        return acc;
      }, {});
  }

  formatCell(row: any, campo: string | null): string {
    if (!campo) return '';
    const valor = row?.[campo] ?? '';
    return String(valor).replace(/\//g, '/ ');
  }

  cellHtml(row: unknown, campo: string | null): string {
    if (!campo) {
      return '';
    }
    const valor = (row as Record<string, unknown>)?.[campo] ?? '';
    return String(valor);
  }

  isRowSelected(row: unknown): boolean {
    const key = this.rowSelectionKey(row);
    return key != null && this._selectedRowKeys.has(key);
  }

  onRowCheckboxChange(row: unknown, checked: boolean): void {
    const key = this.rowSelectionKey(row);
    if (key == null) {
      return;
    }
    if (checked) {
      this._selectedRowKeys.add(key);
    } else {
      this._selectedRowKeys.delete(key);
    }
    this.emitSelectionChange();
  }

  onToggleAllOnPageChange(checked: boolean): void {
    for (const row of this.data ?? []) {
      const key = this.rowSelectionKey(row);
      if (key == null) {
        continue;
      }
      if (checked) {
        this._selectedRowKeys.add(key);
      } else {
        this._selectedRowKeys.delete(key);
      }
    }
    this.emitSelectionChange();
  }

  clearRowSelection(): void {
    if (this._selectedRowKeys.size === 0) {
      return;
    }
    this._selectedRowKeys.clear();
    this.emitSelectionChange();
  }

  rowSelectionKey(row: unknown): string | null {
    if (row == null || typeof row !== 'object') {
      return null;
    }
    const raw = (row as Record<string, unknown>)[this.rowIdField];
    if (raw === null || raw === undefined || raw === '') {
      return null;
    }
    return String(raw);
  }

  private emitSelectionChange(): void {
    this.selectionChange.emit([...this._selectedRowKeys]);
  }
}